import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = [
        "submitButton", "submitText", "loadingText", "errorMessage", "errorText",
        "searchContainer", "searchInput", "productSearchInput", "hiddenInput", "dropdown", "option",
        "companyNameInput", "firstNameInput", "lastNameInput", "phoneInput", "emailInput"
    ]
    static values = { submitUrl: String }

    connect() {
        this.selectedIndex = -1
    }

    // Searchable dropdown methods
    filterOptions(event) {
        const searchTerm = event.target.value.toLowerCase()
        const options = this.optionTargets
        let visibleCount = 0

        options.forEach(option => {
            const text = option.dataset.text.toLowerCase()
            if (text.includes(searchTerm)) {
                option.style.display = 'block'
                visibleCount++
            } else {
                option.style.display = 'none'
            }
        })

        // Show dropdown if there are visible options and input has focus
        if (visibleCount > 0 && event.target === document.activeElement) {
            this.showDropdown()
        } else {
            this.hideDropdown()
        }

        // Reset selection index when filtering
        this.selectedIndex = -1
        this.removeHighlight()
    }

    showOptions(event) {
        // Show all options when input gets focus
        this.optionTargets.forEach(option => {
            option.style.display = 'block'
        })
        this.showDropdown()
    }

    hideOptions(event) {
        // Use setTimeout to allow option click to register before hiding
        setTimeout(() => {
            this.hideDropdown()
        }, 150)
    }

    showDropdown() {
        this.dropdownTarget.classList.remove('hidden')
    }

    hideDropdown() {
        this.dropdownTarget.classList.add('hidden')
        this.selectedIndex = -1
        this.removeHighlight()
    }

    selectOption(event) {
        // Prevent default to avoid focus issues and double-firing on touch devices
        event.preventDefault()

        const option = event.currentTarget
        const value = option.dataset.value
        const text = option.dataset.text

        // Set the input value and hidden field
        this.searchInputTarget.value = text
        this.hiddenInputTarget.value = value

        // Trigger input event to update the floating label in the new component
        this.searchInputTarget.dispatchEvent(new Event('input', { bubbles: true }))

        // Hide dropdown
        this.hideDropdown()

        // Validate form field
        this.searchInputTarget.setCustomValidity('')
    }

    removeHighlight() {
        this.optionTargets.forEach(option => {
            option.classList.remove('bg-gray-100')
        })
    }

    async submit(event) {
        event.preventDefault()

        // Validate that a product is selected
        if (!this.hiddenInputTarget.value) {
            this.searchInputTarget.setCustomValidity('Bitte wählen Sie ein Produkt aus.')
            this.searchInputTarget.reportValidity()
            return
        }

        const formData = new FormData(this.element)

        // Show loading state
        this.submitButtonTarget.disabled = true
        this.submitTextTarget.classList.add('hidden')
        this.loadingTextTarget.classList.remove('hidden')

        // Hide previous messages
        this.errorMessageTarget.classList.add('hidden')

        try {
            const response = await fetch(this.submitUrlValue, {
                method: 'POST',
                body: formData,
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
                }
            })

            if (response.ok) {
                // Parse JSON response
                const responseData = await response.json()
                if (responseData.redirect_url) {
                    window.location.href = responseData.redirect_url
                } else {
                    // Fallback: redirect to configuration page
                    window.location.href = '/partner-vertrag/konfiguration'
                }
            } else {
                // Show field-specific errors if present
                const errorData = await response.json()
                this.clearFieldErrors()
                let fieldErrorShown = false
                if (errorData.errors && Array.isArray(errorData.errors)) {
                    errorData.errors.forEach(msg => {
                        if (msg.includes('Produkt')) {
                            if (this.hasProductSearchInputTarget) {
                                this.setFloatingLabelError(this.productSearchInputTarget, msg)
                            }
                            fieldErrorShown = true
                        } else if (msg.includes('Firmenname')) {
                            if (this.hasCompanyNameInputTarget) {
                                this.setFloatingLabelError(this.companyNameInputTarget, msg)
                            }
                            fieldErrorShown = true
                        } else if (msg.includes('Vorname')) {
                            if (this.hasFirstNameInputTarget) {
                                this.setFloatingLabelError(this.firstNameInputTarget, msg)
                            }
                            fieldErrorShown = true
                        } else if (msg.includes('Nachname')) {
                            if (this.hasLastNameInputTarget) {
                                this.setFloatingLabelError(this.lastNameInputTarget, msg)
                            }
                            fieldErrorShown = true
                        } else if (msg.includes('Telefonnummer')) {
                            if (this.hasPhoneInputTarget) {
                                this.setFloatingLabelError(this.phoneInputTarget, msg)
                            }
                            fieldErrorShown = true
                        } else if (msg.includes('E-Mail')) {
                            if (this.hasEmailInputTarget) {
                                this.setFloatingLabelError(this.emailInputTarget, msg)
                            }
                            fieldErrorShown = true
                        }
                    })
                }
                // Show general error only if no field error was shown
                if (!fieldErrorShown) {
                    this.errorMessageTarget.classList.remove('hidden')
                }
            }
        } catch (error) {
            console.error('Form submission error:', error)
            this.errorMessageTarget.classList.remove('hidden')
        } finally {
            // Reset button state
            this.submitButtonTarget.disabled = false
            this.submitTextTarget.classList.remove('hidden')
            this.loadingTextTarget.classList.add('hidden')
        }

    }

    clearFieldErrors() {
        if (this.hasProductSearchInputTarget) this.setFloatingLabelError(this.productSearchInputTarget, "")
        if (this.hasCompanyNameInputTarget) this.setFloatingLabelError(this.companyNameInputTarget, "")
        if (this.hasFirstNameInputTarget) this.setFloatingLabelError(this.firstNameInputTarget, "")
        if (this.hasLastNameInputTarget) this.setFloatingLabelError(this.lastNameInputTarget, "")
        if (this.hasPhoneInputTarget) this.setFloatingLabelError(this.phoneInputTarget, "")
        if (this.hasEmailInputTarget) this.setFloatingLabelError(this.emailInputTarget, "")
    }

    setFloatingLabelError(input, errorMessage) {
        // Find the parent container with data-floating-label-error
        const container = input.closest('[data-floating-label-error]')
        if (container) {
            container.setAttribute('data-floating-label-error', errorMessage)
        }
    }
}
